/**
 * Wrapper fetch avec ajout automatique du header Authorization et auto-refresh sur 401.
 *
 * Single-flight refresh : si plusieurs requêtes échouent en 401 simultanément, une seule
 * tentative de refresh est lancée et toutes les autres attendent son résultat.
 */

import config from '@ggvie-consulting/config'

import type { TokenResponse } from './types'
import {
  clearSession,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  setSession,
} from './useAuth'

let refreshPromise: Promise<RefreshOutcome> | null = null

/**
 * Callbacks enregistrés par l'app pour réagir à une session expirée non
 * récupérable (refresh token KO). Permet au router/UI de rediriger vers /login
 * et d'afficher un message clair plutôt que de laisser l'utilisateur face à des
 * erreurs cryptiques type "Erreur de transcription (401)".
 *
 * Pattern observer : aucun couplage api.ts → router/store.
 */
type SessionExpiredListener = () => void
const sessionExpiredListeners = new Set<SessionExpiredListener>()

export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener)
  return () => sessionExpiredListeners.delete(listener)
}

function emitSessionExpired(): void {
  for (const listener of sessionExpiredListeners) {
    try {
      listener()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[authFetch] sessionExpired listener failed', err)
    }
  }
}

/** Sentinelle posée sur l'objet Error quand le refresh a échoué. Permet aux
 *  callers de détecter une session expirée et afficher un message dédié sans
 *  parser un code HTTP. */
export class SessionExpiredError extends Error {
  constructor(message = 'Votre session a expiré. Veuillez vous reconnecter.') {
    super(message)
    this.name = 'SessionExpiredError'
  }
}

/** Échec de refresh TRANSITOIRE (API indisponible : déploiement, 5xx, réseau/timeout).
 *  Contrairement à SessionExpiredError, NE déconnecte PAS : la session reste valide, la
 *  requête est seulement à rejouer plus tard. */
export class TransientAuthError extends Error {
  constructor(message = 'Service momentanément indisponible. Réessayez dans un instant.') {
    super(message)
    this.name = 'TransientAuthError'
  }
}

/** Mode maintenance actif (SD-4367) : le backend a répondu 503 avec le marqueur
 *  `{"code":"maintenance"}`. NE déconnecte PAS — la session est conservée pendant
 *  toute la maintenance, l'UI bascule sur la page dédiée. */
export class MaintenanceError extends Error {
  constructor(message = 'Maintenance en cours. Merci de patienter.') {
    super(message)
    this.name = 'MaintenanceError'
  }
}

/** Pattern observer (miroir de `onSessionExpired`) : notifie l'app qu'un appel API a
 *  détecté le mode maintenance, sans coupler api.ts au router/composable. */
type MaintenanceDetectedListener = () => void
const maintenanceDetectedListeners = new Set<MaintenanceDetectedListener>()

export function onMaintenanceDetected(listener: MaintenanceDetectedListener): () => void {
  maintenanceDetectedListeners.add(listener)
  return () => maintenanceDetectedListeners.delete(listener)
}

/**
 * Événement navigateur émis au 503-marqueur, en plus du bus. Montée dans la sandbox (route /ggvie), cette copie n'a
 * pas le routeur : c'est le mode maintenance du SHELL qui bascule la page, et il écoute cet événement (même nom dans
 * `src/maintenance/useMaintenance.ts`, aucun import croisé, ADR-019). Sans lui, la bascule attendait le sondage
 * suivant du shell (≈ 10 s). Sans écouteur dans l'application autonome, qui bascule par son propre bus.
 */
export const MAINTENANCE_DETECTED_DOM_EVENT = 'sapiendo:maintenance-detected'

function emitMaintenanceDetected(): void {
  for (const listener of maintenanceDetectedListeners) {
    try {
      listener()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[authFetch] maintenanceDetected listener failed', err)
    }
  }
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(MAINTENANCE_DETECTED_DOM_EVENT))
  }
}

/** Détecte la réponse 503-maintenance et bascule l'app. `response.clone()` obligatoire
 *  (le corps ne se lit qu'une fois) ; un 502/503 SANS JSON marqueur (blip nginx pendant
 *  un deploy, SD-4352) n'est PAS une maintenance et ressort inchangé. */
async function throwIfMaintenance(response: Response): Promise<void> {
  if (response.status !== 503) return
  let body: unknown
  try {
    body = await response.clone().json()
  } catch {
    return
  }
  if ((body as { code?: string } | null)?.code === 'maintenance') {
    emitMaintenanceDetected()
    throw new MaintenanceError()
  }
}

type RefreshOutcome = 'ok' | 'expired' | 'transient'

const MAX_REFRESH_RETRIES = 3
const REFRESH_RETRY_BASE_MS = 1000

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function _doRefresh(): Promise<RefreshOutcome> {
  const refresh_token = getRefreshToken()
  if (!refresh_token) {
    clearSession()
    emitSessionExpired()
    return 'expired'
  }

  // Distingue permanent (401/403 = refresh révoqué → logout) de transitoire (5xx/réseau =
  // déploiement → retry borné, JAMAIS de déconnexion). C'est ce qui évite la perte de session
  // au déploiement.
  for (let attempt = 0; attempt <= MAX_REFRESH_RETRIES; attempt++) {
    try {
      const response = await fetch(`${config.api.baseURL}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token }),
      })
      if (response.ok) {
        const payload = (await response.json()) as TokenResponse
        setAccessToken(payload.access_token)
        setRefreshToken(payload.refresh_token)
        return 'ok'
      }
      if (response.status === 401 || response.status === 403) {
        clearSession()
        emitSessionExpired()
        return 'expired'
      }
      // 5xx / 429 / autre → indisponibilité transitoire : on tombe dans le retry.
    } catch {
      // Erreur réseau / timeout → transitoire : on tombe dans le retry.
    }
    if (attempt < MAX_REFRESH_RETRIES) {
      await sleep(REFRESH_RETRY_BASE_MS * 2 ** attempt)
    }
  }
  return 'transient'
}

async function ensureRefresh(): Promise<RefreshOutcome> {
  if (refreshPromise) return refreshPromise
  refreshPromise = _doRefresh().finally(() => {
    refreshPromise = null
  })
  return refreshPromise
}

interface AuthFetchOptions extends RequestInit {
  /** Si true, n'inclut PAS le header Authorization (utilisé pour login/refresh/request-access). */
  skipAuth?: boolean
}

export async function authFetch(input: RequestInfo | URL, init: AuthFetchOptions = {}): Promise<Response> {
  const { skipAuth, headers, ...rest } = init
  const baseHeaders: Record<string, string> = {
    ...((headers as Record<string, string>) || {}),
  }
  // Pour les uploads binaires (FormData), le navigateur calcule lui-même
  // `multipart/form-data; boundary=...`. On ne force Content-Type que pour
  // les requêtes JSON.
  if (!(rest.body instanceof FormData) && !baseHeaders['Content-Type']) {
    baseHeaders['Content-Type'] = 'application/json'
  }

  const accessToken = getAccessToken()
  if (!skipAuth && accessToken) {
    baseHeaders['Authorization'] = `Bearer ${accessToken}`
  }

  // SD-3875 — un FormData est consommé après le premier `fetch`. Pour pouvoir
  // refaire la requête après un refresh 401, on clone les entrées dans un
  // FormData neuf à chaque tentative.
  const cloneFormData = (src: FormData): FormData => {
    const copy = new FormData()
    src.forEach((value, key) => copy.append(key, value as Blob | string))
    return copy
  }
  const originalBody = rest.body
  const isFormData = originalBody instanceof FormData
  const buildBody = (): BodyInit | null | undefined =>
    isFormData ? cloneFormData(originalBody as FormData) : originalBody

  let response = await fetch(input, { ...rest, body: buildBody(), headers: baseHeaders })

  if (response.status === 401 && !skipAuth) {
    const outcome = await ensureRefresh()
    if (outcome === 'transient') {
      // API momentanément indisponible (déploiement / réseau) : on NE déconnecte PAS.
      // La requête échoue temporairement et sera rejouée (action utilisateur / watchdog).
      throw new TransientAuthError()
    }
    if (outcome === 'expired') {
      // Session définitivement expirée (refresh invalide/révoqué) ; _doRefresh a déjà émis
      // sessionExpired pour la redirection /login.
      throw new SessionExpiredError()
    }
    const newToken = getAccessToken()
    if (newToken) {
      baseHeaders['Authorization'] = `Bearer ${newToken}`
      response = await fetch(input, { ...rest, body: buildBody(), headers: baseHeaders })
      // Second 401 après refresh réussi → session corrompue côté serveur.
      if (response.status === 401) {
        clearSession()
        emitSessionExpired()
        throw new SessionExpiredError()
      }
    } else {
      // Refresh signalé OK mais token absent (clearSession concurrent : logout,
      // re-entrance). Sans ce garde, on retournerait la Response 401 originale
      // SANS throw ni emit → 401 silencieux, pas de redirection.
      clearSession()
      emitSessionExpired()
      throw new SessionExpiredError()
    }
  }

  // Un seul point de détection maintenance : couvre la réponse initiale ET la réponse
  // rejouée après refresh (un non-admin au token expiré fait 401 → refresh → 503 ici).
  await throwIfMaintenance(response)

  return response
}

/** Login email + mot de passe → stocke la session. */
export async function login(email: string, password: string): Promise<void> {
  const response = await fetch(`${config.api.baseURL}/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}))
    throw new Error(detail?.detail || `Échec de connexion (${response.status})`)
  }
  const payload = (await response.json()) as TokenResponse
  setSession(payload)
}

/**
 * Révoque un refresh token SANS toucher à la session locale.
 *
 * Utile quand on détient un refresh token dont on ne veut PAS (changement de compte SSO
 * refusé) : le jeter côté client le laisserait valide 30 jours côté serveur.
 */
export async function revokeRefreshToken(refresh_token: string | null): Promise<void> {
  if (!refresh_token) return
  try {
    await fetch(`${config.api.baseURL}/v1/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token }),
    })
  } catch {
    // pas grave, l'appelant efface quand même côté client
  }
}

/** Logout : révoque le refresh côté serveur + efface la session. */
export async function logout(): Promise<void> {
  await revokeRefreshToken(getRefreshToken())
  clearSession()
}

export async function requestAccess(email: string, message?: string): Promise<void> {
  const body: Record<string, unknown> = { email }
  if (message && message.trim()) {
    body.message = message.trim()
  }
  const response = await fetch(`${config.api.baseURL}/v1/auth/request-access`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}))
    throw new Error(detail?.detail || `Erreur (${response.status})`)
  }
}

export async function forgotPassword(email: string): Promise<void> {
  const response = await fetch(`${config.api.baseURL}/v1/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}))
    throw new Error(detail?.detail || `Erreur (${response.status})`)
  }
}

/** Le statut HTTP distingue un lien indisponible d’une panne temporaire. */
export class InvalidPasswordLinkError extends Error {
  constructor() {
    super('Lien invalide, expiré ou déjà utilisé.')
    this.name = 'InvalidPasswordLinkError'
  }
}

export async function validatePasswordLink(token: string, signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${config.api.baseURL}/v1/auth/set-password/${encodeURIComponent(token)}`, {
    cache: 'no-store',
    signal,
  })
  if (response.status === 410) throw new InvalidPasswordLinkError()
  if (!response.ok) throw new Error('La vérification du lien est momentanément indisponible.')
}

export async function setPassword(token: string, password: string, signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${config.api.baseURL}/v1/auth/set-password/${encodeURIComponent(token)}`, {
    method: 'POST',
    cache: 'no-store',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  })
  if (response.status === 410) throw new InvalidPasswordLinkError()
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}))
    throw new Error(detail?.detail || `Erreur (${response.status})`)
  }
  const payload = (await response.json()) as TokenResponse
  // Une réponse d’un lien quitté ne doit pas remplacer la session courante.
  signal?.throwIfAborted()
  setSession(payload)
}

export async function fetchMe(): Promise<void> {
  const response = await authFetch(`${config.api.baseURL}/v1/auth/me`)
  if (!response.ok) {
    if (response.status === 401) clearSession()
    return
  }
  const payload = await response.json()
  // Met à jour l'utilisateur local (les agents ont pu changer côté admin).
  const { setUser } = await import('./useAuth')
  setUser(payload)
}

/**
 * SSO entrant depuis la Trilogie Sapiendo (SD-4544, cf. ADR-044).
 *
 * Le jeton arrive en query string (contrat Phenix) mais repart d'ici dans le CORPS d'un
 * POST : une query string atterrit dans les logs d'accès (uvicorn, nginx via le Referer
 * des sous-ressources) et dans Sentry, dont le scrub ne couvre que les en-têtes.
 */
/** Codes renvoyés par le serveur. L'allowlist runtime et le type dérivent de cette liste. */
const SSO_SERVER_CODES = ['invalid_token', 'replayed', 'no_access', 'sso_unavailable'] as const

/** Codes serveur + ceux que le client synthétise lui-même. */
export type SsoFailureCode = (typeof SSO_SERVER_CODES)[number] | 'maintenance' | 'network'

export class SsoExchangeError extends Error {
  /**
   * @param reference Référence courte fournie par le serveur sur les refus survenus APRÈS
   * vérification du jeton, pour que le support retrouve l'échange dans les journaux. Le
   * jeton étant chiffré, le front ne peut plus la déduire lui-même.
   */
  constructor(
    readonly code: SsoFailureCode,
    readonly reference: string | null = null,
  ) {
    super(`sso_exchange_failed:${code}`)
    this.name = 'SsoExchangeError'
  }
}

/** Échange un jeton SSO Phenix contre une session. Ne pose PAS la session (l'appelant décide). */
export async function ssoExchange(token: string): Promise<TokenResponse> {
  let response: Response
  try {
    response = await fetch(`${config.api.baseURL}/v1/auth/sso/phenix`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
  } catch {
    throw new SsoExchangeError('network')
  }

  if (!response.ok) {
    // Détection de maintenance déléguée au helper du module : c'est lui la SSoT du
    // marqueur `{"code":"maintenance"}`, et il prévient `useMaintenance` au passage.
    // Chemin aujourd'hui inatteignable (`/api/v1/auth/` est allowlisté du gate) — gardé
    // comme défense en profondeur si l'allowlist se resserre.
    try {
      await throwIfMaintenance(response)
    } catch (error) {
      if (error instanceof MaintenanceError) throw new SsoExchangeError('maintenance')
      throw error
    }

    const body = await response.json().catch(() => ({}))
    const code = body?.detail?.code
    const reference = body?.detail?.reference
    throw new SsoExchangeError(
      SSO_SERVER_CODES.includes(code) ? (code as SsoFailureCode) : 'invalid_token',
      typeof reference === 'string' && reference ? reference : null,
    )
  }

  return (await response.json()) as TokenResponse
}
